import { Signal } from "signals";
import gameState from "slotworks/model/game-state";
import Gameplay from "slotworks/model/gameplay/gameplay";
import ClusterResult from "slotworks/model/gameplay/records/results/cluster-result";
import SpinRecord from "slotworks/model/gameplay/records/spin-record";
import settingsModel from "slotworks/model/settings/settings-model";
import slotDefinition from "slotworks/model/slot-definition";
import { arrayOfValues } from "slotworks/utils/collection-utils";
import CancelGroup from "slotworks/utils/contracts/cancel-group";
import Contract from "slotworks/utils/contracts/contract";
import Parallel from "slotworks/utils/contracts/parallel";
import Point from "slotworks/utils/geom/point";
import ReelComponent from "../../reel-component";
import AbstractReelTransition from "../abstract-reel-transition";
import ReelCascader from "./reel-cascader";

export class CascadeTransition extends AbstractReelTransition {

    public static CASCADE_DELAY: number = 500;
    public static CASCADE_DELAY_QUICK_SPIN: number = 300;
    public static CASCADE_DELAY_TURBO_SPIN: number = 150;

    public onCascadeStart: Signal = new Signal();
    public onCascadeEnd: Signal = new Signal();

    protected cascaders: ReelCascader[] = [];
    protected cancelGroup: CancelGroup = new CancelGroup();

    public init(reels: ReelComponent[], stops: number[], reelset: string[][]): void {
        super.init(reels, stops, reelset);
        this.reels = reels;
        this.cascaders = [];

        reels.forEach((reel: ReelComponent, i: number) => {
            const cascader = this.getNewCascader(reel, stops, reelset);
            this.cascaders.push(cascader);
            cascader.onCascadeStart.add((index: number) => this.cascadeStartHandler(index));
            cascader.onCascadeEnd.add((index: number) => this.cascadeEndHandler(index));
        });
    }

    public destroy(): void {
        this.cascaders.forEach((cascader: ReelCascader) => {
            cascader.onCascadeStart.remove((index: number) => this.cascadeStartHandler(index));
            cascader.onCascadeEnd.remove((index: number) => this.cascadeEndHandler(index));
        });
    }

    public stop(cascadeRecord: SpinRecord, quickSpin: boolean = false): Contract<void> {
        const spinRecord: SpinRecord = this.getRecord();
        const cascadeSequence: Array<() => Contract<void>> = [];
        const clusterResults: ClusterResult[] = spinRecord.getResultsOfType(ClusterResult);
        const turboSpin: boolean = settingsModel.getTurboSpin();
        cascadeSequence.push(() => this.cascade(clusterResults, cascadeRecord.grid, quickSpin, turboSpin));
        return this.cancelGroup.sequence(cascadeSequence);
    }

    public skip(cascadeRecord: SpinRecord): Contract<void> {
        const skipContracts: Array<() => Contract<void>> = [];
        this.cancelGroup.cancel();
        this.cascaders.forEach((cascader: ReelCascader) => {
            const contract: Contract<void> = cascader.skip(cascadeRecord);
            skipContracts.push(() => contract);
        });
        return new Parallel(skipContracts);
    }

    protected getRecord(): SpinRecord {
        const gameplay: Gameplay = gameState.getCurrentGame();
        const parent: SpinRecord = gameplay.getCurrentRecordParent() as SpinRecord;
        if (parent) {
            return parent;
        }
        return gameplay.getCurrentRecord() as SpinRecord;
    }

    protected getNewCascader(reel: ReelComponent, stops: number[], reelset: string[][]) {
        return new ReelCascader(reel, stops, reelset);
    }

    protected cascade(results: ClusterResult[], grid: string[][], quickSpin: boolean, turboSpin: boolean): Contract<void> {
        const symbolsPerReel: Point[][] = arrayOfValues<Point[]>(slotDefinition.matrixGrid.length, []);
        results.forEach((result: ClusterResult) => {
            let allPositions: Point[] = result.positions;
            if (result.affectedPositions) {
                allPositions = allPositions.concat(result.affectedPositions);
            }
            allPositions.sort((a: Point, b: Point) => a.y - b.y);
            allPositions.forEach((point: Point) => symbolsPerReel[point.x].push(point));
        });

        const cascadeInContracts: Array<() => Contract<void>> = [];
        const delay: number = turboSpin ? CascadeTransition.CASCADE_DELAY_TURBO_SPIN : quickSpin ? CascadeTransition.CASCADE_DELAY_QUICK_SPIN : CascadeTransition.CASCADE_DELAY;
        symbolsPerReel.forEach((points: Point[], index: number) => {
            points.sort((a: Point, b: Point) => a.y - b.y);
            if (points.length > 0) {
                cascadeInContracts.push(() => Contract.getDelayedContract(delay, () => this.cascaders[index].cascadeSymbols(points, grid[index], quickSpin, turboSpin)));
            }
        });

        return this.cancelGroup.parallel<void>(cascadeInContracts);
    }

    private cascadeStartHandler(reel: number): void {
        this.onCascadeStart.dispatch(reel);
    }

    private cascadeEndHandler(reel: number): void {
        this.onCascadeEnd.dispatch(reel);
    }
}
